/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PROGRAM TRAINING & MARKETING RESOURCES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The previous model was `{ id, name, type: 'file' | 'text', url | text }` —
 * no description, no category, no ordering, no publish state, no video or link
 * support, and no way to edit an entry after adding it (only add and delete).
 *
 * This module defines the richer resource object and normalises every legacy
 * record into it, so the Program Details page can offer a real content
 * management experience without a data migration.
 */

export type ResourceKind = 'file' | 'video' | 'link' | 'article' | 'image';
export type ResourceCollection = 'training' | 'marketing';

export interface ProgramResource {
  id: string;
  collection: ResourceCollection;
  kind: ResourceKind;
  title: string;
  description?: string;
  /** Free-form grouping, e.g. "Onboarding", "Brochures". */
  category?: string;
  /** For file / image / video / link resources. */
  url?: string;
  /** Original file name, shown when the URL is opaque. */
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  /** Rich text body for `article` resources. */
  content?: string;
  /** Optional thumbnail for cards and previews. */
  thumbnailUrl?: string;
  /** Unpublished resources stay editable but are hidden from portals. */
  published: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export const RESOURCE_KIND_LABELS: Record<ResourceKind, string> = {
  file: 'File / document',
  image: 'Image',
  video: 'Video',
  link: 'External link',
  article: 'Rich text article',
};

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i;
const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(\?|$)/i;
const VIDEO_HOST = /(youtube\.com|youtu\.be|vimeo\.com|loom\.com|wistia\.com)/i;

function newId(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
      : Math.random().toString(36).slice(2, 12);
  return `res_${rand}`;
}

/** Best-effort kind detection for legacy rows that only stored a URL. */
export function detectResourceKind(input: {
  url?: string;
  mimeType?: string;
  hasContent?: boolean;
}): ResourceKind {
  if (input.hasContent && !input.url) return 'article';
  const url = input.url || '';
  const mime = input.mimeType || '';
  if (mime.startsWith('image/') || IMAGE_EXT.test(url)) return 'image';
  if (mime.startsWith('video/') || VIDEO_EXT.test(url) || VIDEO_HOST.test(url)) return 'video';
  if (!url) return 'article';
  // A storage URL points at a file; anything else is treated as a link.
  return /firebasestorage|storage\.googleapis|\.pdf|\.docx?|\.xlsx?|\.pptx?|\.zip/i.test(url)
    ? 'file'
    : 'link';
}

/**
 * Normalises the legacy `trainingMaterials` / `marketingMaterials` arrays
 * (`{id, name, type: 'file'|'text', url|text}`) and any modern records into
 * the full ProgramResource shape.
 */
export function normaliseResources(value: unknown, collection: ResourceCollection): ProgramResource[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index): ProgramResource | null => {
      if (!entry || typeof entry !== 'object') return null;
      const raw = entry as Record<string, unknown>;

      const title = String(raw.title ?? raw.name ?? raw.label ?? '').trim();
      const url = String(raw.url ?? raw.downloadUrl ?? raw.href ?? '').trim();
      const content = String(raw.content ?? raw.text ?? raw.body ?? '').trim();
      if (!title && !url && !content) return null;

      const legacyType = String(raw.type ?? '').toLowerCase();
      const kind: ResourceKind =
        legacyType === 'text'
          ? 'article'
          : (['file', 'video', 'link', 'article', 'image'] as string[]).includes(legacyType)
            ? (legacyType as ResourceKind)
            : detectResourceKind({ url, mimeType: String(raw.mimeType ?? ''), hasContent: !!content });

      return {
        id: String(raw.id ?? '') || newId(),
        collection,
        kind,
        title: title || (kind === 'article' ? 'Untitled article' : 'Untitled resource'),
        description: raw.description ? String(raw.description) : undefined,
        category: raw.category ? String(raw.category) : undefined,
        url: url || undefined,
        fileName: raw.fileName ? String(raw.fileName) : undefined,
        mimeType: raw.mimeType ? String(raw.mimeType) : undefined,
        sizeBytes: typeof raw.sizeBytes === 'number' ? raw.sizeBytes : undefined,
        content: content || undefined,
        thumbnailUrl: raw.thumbnailUrl ? String(raw.thumbnailUrl) : undefined,
        // Legacy rows had no publish concept — treat them as published so
        // nothing disappears from the portals after the upgrade.
        published: raw.published === undefined ? true : raw.published !== false,
        sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : index,
        createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
        updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
      };
    })
    .filter((r): r is ProgramResource => r !== null)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((resource, index) => ({ ...resource, sortOrder: index }));
}

export function createResource(
  collection: ResourceCollection,
  kind: ResourceKind = 'file'
): ProgramResource {
  return {
    id: newId(),
    collection,
    kind,
    title: '',
    published: true,
    sortOrder: 0,
    createdAt: new Date().toISOString(),
  };
}

export function moveResource(
  resources: ProgramResource[],
  from: number,
  to: number
): ProgramResource[] {
  if (from === to || from < 0 || to < 0 || from >= resources.length || to >= resources.length) {
    return resources;
  }
  const next = [...resources];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((resource, index) => ({ ...resource, sortOrder: index }));
}

/** Only published resources reach applicant / referrer / employer portals. */
export function publishedResources(resources: ProgramResource[] | undefined): ProgramResource[] {
  return (resources || []).filter((resource) => resource.published);
}

export function groupByCategory(
  resources: ProgramResource[]
): Array<{ category: string; resources: ProgramResource[] }> {
  const groups = new Map<string, ProgramResource[]>();
  for (const resource of resources) {
    const key = resource.category?.trim() || 'General';
    const list = groups.get(key);
    if (list) list.push(resource);
    else groups.set(key, [resource]);
  }
  return Array.from(groups.entries())
    .map(([category, list]) => ({ category, resources: list }))
    .sort((a, b) => a.category.localeCompare(b.category));
}

/** Converts common video URLs into an embeddable form for previews. */
export function toEmbedUrl(url: string | undefined): string | null {
  if (!url) return null;
  const youtube = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/i);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  const loom = url.match(/loom\.com\/share\/([\w-]+)/i);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  if (VIDEO_EXT.test(url)) return url;
  return null;
}

export function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
